import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DsButtonIntent, DsModalService, DsModalVariant } from '@levelaccess/design-system';

import { $user } from '@shared/constants/user';
import { IMfaStatusResponse } from '@shared/interfaces/user.interface';
import { RoutingService } from '../../../../../services/routing.service';
import { AppConfigService } from '../../../../../services/app-config.service';
import { UserService } from '../../../../../services/user.service';
import { NotificationService } from '../../../../../services/notification.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { ChangePasswordComponent } from '../../../../../components/change-password/from-settings/change-password.component';
import { NotificationPosition } from '../../../../../models/notification.model';
import { MfaRegistrationModalComponent } from '../profile/mfa-registration-modal/mfa-registration-modal.component';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  standalone: false,
})
export class AccountSettingsComponent implements OnInit, OnDestroy {
  @Input() displayHeader: boolean = true;
  @Input() modal: boolean = false;

  @Output() readonly passwordChangeSuccess: EventEmitter<void> = new EventEmitter<void>();
  @Output() readonly passwordChangeError: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(ChangePasswordComponent) changePasswordComponent: ChangePasswordComponent;

  readonly mfaStatusResponse$: BehaviorSubject<IMfaStatusResponse> = new BehaviorSubject<IMfaStatusResponse>(null);
  public pageTitle: string;
  protected readonly DsButtonIntent: typeof DsButtonIntent = DsButtonIntent;

  private readonly subscription: Subscription = new Subscription();

  constructor(
    private routingService: RoutingService,
    private appConfigService: AppConfigService,
    private userService: UserService,
    private dsModalService: DsModalService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private loadErrorHandler: LoadErrorHandler,
  ) {
    this.pageTitle = '';
  }

  public get isNotUsingExternalIdp(): boolean {
    return !this.appConfigService.isUsingExternalIdp();
  }

  public ngOnInit(): void {
    this.pageTitle = this.routingService.getPageTitle();
    this.updateMfaStatus();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public enableMfa(): void {
    const modalRef = this.dsModalService.open(MfaRegistrationModalComponent, DsModalVariant.center);

    this.subscription.add(
      modalRef.componentInstance.onMfaEnabled.subscribe(() => {
        this.notificationService.success(this.translateService.instant('mfa_verified_successfully'), NotificationPosition.Toast);
        this.updateMfaStatus();
      }),
    );
  }

  public disableMfa(): void {
    this.subscription.add(
      this.userService.disableMfa().subscribe({
        next: (): void => {
          this.notificationService.success(
            this.translateService.instant('mfa_disabled_successfully'),
            NotificationPosition.Toast,
          );
          this.updateMfaStatus();
        },
        error: (error: HttpErrorResponse): void => {
          this.loadErrorHandler.handleError('mfa_failed_to_disable', error, AccountSettingsComponent.name);
        },
      }),
    );
  }

  private updateMfaStatus(): void {
    this.subscription.add(
      this.userService.getMfaStatus().subscribe({
        next: (response: IMfaStatusResponse): void => this.mfaStatusResponse$.next(response),
        error: (error: HttpErrorResponse): void =>
          this.loadErrorHandler.handleError('mfa_failed_to_get_status', error, AccountSettingsComponent.name),
      }),
    );
  }

  public submitChangePassword(): void {
    if (this.modal && this.arePasswordFieldsEmpty()) {
      return;
    }
    this.changePasswordComponent?.onSubmitPasswordChange();
  }

  public arePasswordFieldsEmpty(): boolean {
    const form = this.changePasswordComponent?.formChangePassword;
    if (!form) {
      return true;
    }
    const newPasswordValue: string = form.get($user.newPassword)?.value ?? '';
    const currentPasswordValue: string = form.get($user.currentPassword)?.value ?? '';
    return !currentPasswordValue.trim() && !newPasswordValue.trim();
  }
}
